import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Save, Send, Clock, ChevronDown, RotateCcw, ExternalLink, Eye, Copy, Check } from "lucide-react";
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Interface para definir a estrutura de uma versão
export interface QuotationVersion {
  id: string;
  version: 'work' | 'published' | 'history';
  published_at?: string | null;
  last_updated_at?: string | null;
}

interface PublishButtonsProps {
  onSave: () => void;
  onPublish: () => void;
  onOpenVersion: (version: QuotationVersion) => void;
  onPreviewVersion?: (version: QuotationVersion) => void;
  onRepublishVersion?: (version: QuotationVersion) => void;
  workVersion?: QuotationVersion;
  publishedVersion?: QuotationVersion;
  historyVersion?: QuotationVersion;
  isSaving?: boolean;
}

export function PublishButtons({
  onSave,
  onPublish,
  onOpenVersion,
  onPreviewVersion,
  onRepublishVersion,
  workVersion,
  publishedVersion,
  historyVersion,
  isSaving = false
}: PublishButtonsProps) {
  
  // Estado para controlar qual versão está com "Copiado!" exibido
  const [copiedVersion, setCopiedVersion] = useState<string | null>(null);
  
  // Formatação da data para exibição
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (e) {
      return 'Data inválida';
    }
  };
  
  // Função para copiar o link da cotação para o clipboard
  const copyLinkToClipboard = (versionId: string) => {
    // Construir a URL do QuotationView
    const quotationViewUrl = `${window.location.origin}/quotation/${versionId}`;
    
    // Copiar para o clipboard
    navigator.clipboard.writeText(quotationViewUrl)
      .then(() => {
        // Mostrar feedback visual
        setCopiedVersion(versionId);
        
        // Reset após 2 segundos
        setTimeout(() => {
          setCopiedVersion(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Erro ao copiar para o clipboard:', err);
      });
  };
  
  const hasVersions = !!publishedVersion || !!historyVersion;
  
  return (
    <div className="flex items-center gap-2">
      {/* Botão Salvar */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              size="icon"
              disabled={isSaving} 
              className="bg-voah-teal hover:bg-voah-teal/90 text-white h-10 w-10"
              onClick={onSave}
            >
              {isSaving ? (
                <span className="animate-spin">
                  <Clock className="h-4 w-4" />
                </span>
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Salvar cotação</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Botão Publicar com Dropdown */}
      <DropdownMenu>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button 
                  size="icon"
                  disabled={isSaving} 
                  className="bg-voah-coral hover:bg-voah-coral/90 text-white h-10 w-10"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Publicar cotação</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={onPublish}>
            <Send className="h-4 w-4 mr-2" />
            <span>Publicar</span>
          </DropdownMenuItem>
          
          {hasVersions && (
            <>
              <DropdownMenuSeparator />
              
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Versões</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-72">
                    {workVersion && (
                      <div className="p-2 text-sm">
                        <div className="font-medium">Versão de trabalho</div>
                        <div className="text-gray-500 text-xs mb-2">
                          Último salvamento: {formatDate(workVersion.last_updated_at)}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 text-xs flex-1 flex items-center justify-center gap-1"
                            onClick={() => onOpenVersion(workVersion)}
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>Editar</span>
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {publishedVersion && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="p-2 text-sm">
                          <div className="font-medium">Versão publicada</div>
                          <div className="text-gray-500 text-xs mb-2">
                            Publicada em: {formatDate(publishedVersion.published_at)}
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => onOpenVersion(publishedVersion)}
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span>Abrir</span>
                            </Button>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => onPreviewVersion ? onPreviewVersion(publishedVersion) : window.open(`/quotation/${publishedVersion.id}?from=editor`, '_blank')}
                            >
                              <Eye className="h-3 w-3" />
                              <span>Ver</span>
                            </Button>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => copyLinkToClipboard(publishedVersion.id)}
                            >
                              {copiedVersion === publishedVersion.id ? (
                                <>
                                  <Check className="h-3 w-3 text-green-500" />
                                  <span className="text-green-500">Copiado</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3" />
                                  <span>Link</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {historyVersion && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="p-2 text-sm">
                          <div className="font-medium">Versão histórica</div>
                          <div className="text-gray-500 text-xs mb-2">
                            Publicada em: {formatDate(historyVersion.published_at)}
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => onOpenVersion(historyVersion)}
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span>Abrir</span>
                            </Button>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => onPreviewVersion ? onPreviewVersion(historyVersion) : window.open(`/quotation/${historyVersion.id}?from=editor`, '_blank')}
                            >
                              <Eye className="h-3 w-3" />
                              <span>Ver</span>
                            </Button>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs flex items-center justify-center gap-1"
                              onClick={() => copyLinkToClipboard(historyVersion.id)}
                            >
                              {copiedVersion === historyVersion.id ? (
                                <>
                                  <Check className="h-3 w-3 text-green-500" />
                                  <span className="text-green-500">OK</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3" />
                                  <span>Link</span>
                                </>
                              )}
                            </Button>
                            
                            {onRepublishVersion && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 text-xs flex items-center justify-center gap-1"
                                onClick={() => onRepublishVersion(historyVersion)}
                              >
                                <RotateCcw className="h-3 w-3" />
                                <span>Usar</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
} 